import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import toast from 'react-hot-toast';

interface ChangePasswordFormProps {
  onSuccess?: () => void;
  user?: {
    hasPassword?: boolean;
  };
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ onSuccess, user }) => {
  const { t } = useLanguage();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmNewPassword?: string;
    general?: string;
  }>({});

  const clearErrors = () => {
    setErrors({});
  };

  const validateField = (fieldName: string, value: string) => {
    const newErrors = { ...errors };
    
    if (fieldName === 'currentPassword') {
      if (!value.trim()) {
        newErrors.currentPassword = t('auth.fieldRequired');
      } else {
        delete newErrors.currentPassword;
      }
    }
    
    if (fieldName === 'newPassword') {
      if (!value.trim()) {
        newErrors.newPassword = t('auth.fieldRequired');
      } else if (value.length < 6) {
        newErrors.newPassword = t('auth.passwordMinLength').replace('{current}', value.length.toString());
      } else {
        delete newErrors.newPassword;
      }
    }
    
    if (fieldName === 'confirmNewPassword') {
      if (!value.trim()) {
        newErrors.confirmNewPassword = t('auth.fieldRequired');
      } else if (value !== newPassword) {
        newErrors.confirmNewPassword = t('auth.passwordsDoNotMatch');
      } else {
        delete newErrors.confirmNewPassword;
      }
    }
    
    setErrors(newErrors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    
    // Validazione client-side
    const newErrors: typeof errors = {};
    
    // Validazione campi obbligatori
    if (!currentPassword.trim()) {
      newErrors.currentPassword = t('auth.fieldRequired');
    }
    
    if (!newPassword.trim()) {
      newErrors.newPassword = t('auth.fieldRequired');
    } else if (newPassword.length < 6) {
      newErrors.newPassword = t('auth.passwordMinLength').replace('{current}', newPassword.length.toString());
    }
    
    if (!confirmNewPassword.trim()) {
      newErrors.confirmNewPassword = t('auth.fieldRequired');
    } else if (newPassword !== confirmNewPassword) {
      newErrors.confirmNewPassword = t('auth.passwordsDoNotMatch');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      toast.success(t('account.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowForm(false);
      clearErrors();
      onSuccess?.();
      
      // Logout automatico e redirect al login dopo il cambio password
      setTimeout(() => {
        logout();
        navigate('/login');
        toast.success(t('account.passwordChangedRedirect'));
      }, 1500);
    } catch (error: any) {
      console.error('Error changing password:', error);
      
      // Gestione specifica per "current password is wrong"
      if (error.message && error.message.toLowerCase().includes('current password')) {
        setErrors({ currentPassword: t('account.currentPasswordWrong') });
      } else {
        setErrors({ general: t('account.passwordChangeError') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Se l'utente non ha una password (es. login con social), mostra un messaggio informativo
  if (user && user.hasPassword === false) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-700">{t('account.socialLoginNoPassword') || 'This profile was created via Google sign-in and does not have a password to change.'}</p>
      </div>
    );
  }

  if (!showForm) {
    return (
      <div className="mt-4">
        <button
          onClick={() => setShowForm(true)}
          className="text-blue-600 hover:text-blue-800 text-sm underline"
        >
          {t('account.changePassword')}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
      <h3 className="text-lg font-medium text-gray-900 mb-3">
        {t('account.changePassword')}
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        {t('account.changePasswordDescription')}
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
            {t('account.currentPassword')}
          </label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                validateField('currentPassword', e.target.value);
              }}
              onBlur={(e) => validateField('currentPassword', e.target.value)}
              required
              className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.currentPassword ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              aria-label={showCurrent ? 'Hide password' : 'Show password'}
              onClick={() => setShowCurrent((v) => !v)}
              className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {showCurrent ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7 0-1.07.42-2.164 1.125-3.175m3.027-2.64A9.96 9.96 0 0112 5c5 0 9 4 9 7 0 1.07-.42 2.164-1.125 3.175M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.477 10.477A3 3 0 0012 15a3 3 0 002.121-5.121M9.88 9.88L4.222 4.222M19.778 19.778L14.12 14.12M6.343 6.343A9.956 9.956 0 0012 5c5 0 9 4 9 7 0 1.07-.42 2.164-1.125 3.175" />
                )}
              </svg>
            </button>
          </div>
          {errors.currentPassword && (
            <p className="text-red-500 text-sm mt-1">{errors.currentPassword}</p>
          )}
        </div>

        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
            {t('account.newPassword')}
          </label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              id="newPassword"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                validateField('newPassword', e.target.value);
                if (confirmNewPassword) {
                  validateField('confirmNewPassword', confirmNewPassword);
                }
              }}
              onBlur={(e) => validateField('newPassword', e.target.value)}
              required
              minLength={6}
              className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.newPassword ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              aria-label={showNew ? 'Hide password' : 'Show password'}
              onClick={() => setShowNew((v) => !v)}
              className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {showNew ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7 0-1.07.42-2.164 1.125-3.175m3.027-2.64A9.96 9.96 0 0112 5c5 0 9 4 9 7 0 1.07-.42 2.164-1.125 3.175M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.477 10.477A3 3 0 0012 15a3 3 0 002.121-5.121M9.88 9.88L4.222 4.222M19.778 19.778L14.12 14.12M6.343 6.343A9.956 9.956 0 0012 5c5 0 9 4 9 7 0 1.07-.42 2.164-1.125 3.175" />
                )}
              </svg>
            </button>
          </div>
          {errors.newPassword && (
            <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700 mb-1">
            {t('account.confirmNewPassword')}
          </label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              id="confirmNewPassword"
              value={confirmNewPassword}
              onChange={(e) => {
                setConfirmNewPassword(e.target.value);
                validateField('confirmNewPassword', e.target.value);
              }}
              onBlur={(e) => validateField('confirmNewPassword', e.target.value)}
              required
              className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.confirmNewPassword ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {showConfirm ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7 0-1.07.42-2.164 1.125-3.175m3.027-2.64A9.96 9.96 0 0112 5c5 0 9 4 9 7 0 1.07-.42 2.164-1.125 3.175M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.477 10.477A3 3 0 0012 15a3 3 0 002.121-5.121M9.88 9.88L4.222 4.222M19.778 19.778L14.12 14.12M6.343 6.343A9.956 9.956 0 0012 5c5 0 9 4 9 7 0 1.07-.42 2.164-1.125 3.175" />
                )}
              </svg>
            </button>
          </div>
          {errors.confirmNewPassword && (
            <p className="text-red-500 text-sm mt-1">{errors.confirmNewPassword}</p>
          )}
        </div>

        {errors.general && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{errors.general}</p>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-200"
          >
            {isLoading ? t('account.saving') : t('account.save')}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowForm(false);
              setCurrentPassword('');
              setNewPassword('');
              setConfirmNewPassword('');
              clearErrors();
            }}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
          >
            {t('account.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}; 